import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Loader2, Brain, Store, Check, X, Package, AlertTriangle, Info, CheckSquare, Square } from 'lucide-react';
import { useAppStore } from '../../shared/store';
import { generateId } from '../../shared/utils/id';
interface AIGeneratorProps {
  onClose: () => void;
  onGenerate: (items: any[]) => void;
}
export function AIListGenerator({
  onClose,
  onGenerate
}: AIGeneratorProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedItems, setGeneratedItems] = useState<any[]>([]);
  const [mode, setMode] = useState<'smart' | 'basic'>('smart');
  const [error, setError] = useState('');
  const [step, setStep] = useState<'initial' | 'generating' | 'review'>('initial');
  const [selectedItems, setSelectedItems] = useState<{
    [key: string]: boolean;
  }>({});
  const {
    inventory,
    activeDeals,
    stores,
    shoppingList
  } = useAppStore();
  const generateList = async () => {
    setError('');
    setIsGenerating(true);
    setStep('generating');
    try {
      const activeStores = stores.filter(store => store.isActive !== false);
      if (activeStores.length === 0) {
        setError('Selecteer eerst minimaal één winkel in de instellingen');
        setStep('initial');
        setIsGenerating(false);
        return;
      }
      // Simulate AI thinking time
      await new Promise(resolve => setTimeout(resolve, 2000));
      const suggestedItemsMap = new Map();
      const currentShoppingListMap = new Map(shoppingList.map(item => [item.name.toLowerCase(), item]));
      // 1. Low stock items
      const itemsToRestock = inventory.filter(item => {
        const existingListItem = currentShoppingListMap.get(item.name.toLowerCase());
        if (existingListItem) return false;
        const currentTotal = item.quantity + (existingListItem?.quantity || 0);
        const needsRestocking = currentTotal <= item.minQuantity;
        const quantityNeeded = item.maxQuantity - currentTotal;
        return needsRestocking && quantityNeeded > 0;
      });
      itemsToRestock.forEach(item => {
        const existingListItem = currentShoppingListMap.get(item.name.toLowerCase());
        const currentTotal = item.quantity + (existingListItem?.quantity || 0);
        const activeItemDeals = activeDeals.filter(deal => deal.itemId === item.id && new Date(deal.endDate) > new Date() && activeStores.some(store => store.id === deal.retailerId));
        const bestDeal = activeItemDeals.reduce((best: any, current: any) => {
          const bestSavings = best ? best.originalPrice - best.discountedPrice : 0;
          const currentSavings = current.originalPrice - current.discountedPrice;
          return currentSavings > bestSavings ? current : best;
        }, null);
        const store = bestDeal ? activeStores.find(s => s.id === bestDeal.retailerId) : activeStores[0];
        if (!store) return;
        const spaceAvailable = item.maxQuantity - currentTotal;
        const dealMinQuantity = bestDeal?.minQuantity || 1;
        const dealMaxQuantity = bestDeal?.maxQuantity || Infinity;
        const quantityToBuy = Math.min(spaceAvailable, dealMaxQuantity, Math.max(dealMinQuantity, item.maxQuantity - item.minQuantity));
        if (quantityToBuy > 0) {
          suggestedItemsMap.set(item.name.toLowerCase(), {
            id: generateId(),
            itemId: item.id,
            name: item.name,
            quantity: quantityToBuy,
            unit: item.unit,
            category: item.category,
            reason: `Voorraad laag (${item.quantity}/${item.minQuantity})`,
            deal: bestDeal ? `${((bestDeal.originalPrice - bestDeal.discountedPrice) / bestDeal.originalPrice * 100).toFixed(0)}% korting` : null,
            estimatedPrice: bestDeal ? bestDeal.discountedPrice : item.price,
            originalPrice: bestDeal ? bestDeal.originalPrice : item.price,
            store: store?.name || 'Overig'
          });
        }
      });
      if (mode === 'smart') {
        // Bulk deals
        const bulkDeals = activeDeals.filter(deal => deal.type === 'bulk' && new Date(deal.endDate) > new Date() && deal.isActive && activeStores.some(store => store.id === deal.retailerId));
        bulkDeals.forEach(deal => {
          const item = inventory.find(i => i.id === deal.itemId);
          if (!item) return;
          const existingListItem = currentShoppingListMap.get(item.name.toLowerCase());
          if (existingListItem) return;
          const currentTotal = item.quantity + (existingListItem?.quantity || 0);
          const spaceAvailable = item.maxQuantity - currentTotal;
          if (spaceAvailable >= (deal.minQuantity || 1)) {
            const savingsPercentage = (deal.originalPrice - deal.discountedPrice) / deal.originalPrice * 100;
            if (savingsPercentage >= 20) {
              const existingItem = suggestedItemsMap.get(item.name.toLowerCase());
              const store = stores.find(s => s.id === deal.retailerId);
              if (!existingItem || savingsPercentage > parseFloat(existingItem.deal || '0')) {
                suggestedItemsMap.set(item.name.toLowerCase(), {
                  id: generateId(),
                  itemId: item.id,
                  name: item.name,
                  quantity: deal.minQuantity || 1,
                  unit: item.unit,
                  category: item.category,
                  reason: `Bulk aanbieding (${savingsPercentage.toFixed(0)}% korting)`,
                  deal: `${savingsPercentage.toFixed(0)}% korting`,
                  estimatedPrice: deal.discountedPrice,
                  originalPrice: deal.originalPrice,
                  store: store?.name || 'Overig'
                });
              }
            }
          }
        });
        // Good deals for items not low on stock but have space
        const itemsWithDeals = inventory.filter(item => {
          const existingListItem = currentShoppingListMap.get(item.name.toLowerCase());
          if (existingListItem) return false;
          const currentTotal = item.quantity + (existingListItem?.quantity || 0);
          const willRunLowSoon = currentTotal <= item.minQuantity * 1.5;
          const hasSpaceForMore = currentTotal < item.maxQuantity;
          const hasGoodDeal = activeDeals.some(deal => deal.itemId === item.id && new Date(deal.endDate) > new Date() && (deal.originalPrice - deal.discountedPrice) / deal.originalPrice > 0.2);
          return willRunLowSoon && hasGoodDeal && hasSpaceForMore;
        }).map(item => {
          const existingListItem = currentShoppingListMap.get(item.name.toLowerCase());
          const currentTotal = item.quantity + (existingListItem?.quantity || 0);
          const deal = activeDeals.filter(d => d.itemId === item.id && new Date(d.endDate) > new Date()).reduce((best: any, current: any) => {
            const bestSavings = best ? best.originalPrice - best.discountedPrice : 0;
            const currentSavings = current.originalPrice - current.discountedPrice;
            return currentSavings > bestSavings ? current : best;
          }, null);
          if (!deal) return null;
          const store = stores.find(s => s.id === deal.retailerId);
          const spaceAvailable = item.maxQuantity - currentTotal;
          const quantityToBuy = Math.min(spaceAvailable, deal.maxQuantity || Infinity, Math.max(deal.minQuantity || 1, Math.ceil((item.maxQuantity - item.minQuantity) / 2)));
          if (quantityToBuy <= 0) return null;
          const existingItem = suggestedItemsMap.get(item.name.toLowerCase());
          if (existingItem) {
            const existingDiscount = existingItem.deal ? parseFloat(existingItem.deal) : 0;
            const newDiscount = (deal.originalPrice - deal.discountedPrice) / deal.originalPrice * 100;
            if (newDiscount > existingDiscount) {
              return {
                ...existingItem,
                reason: `${existingItem.reason} + Betere aanbieding`,
                deal: `${newDiscount.toFixed(0)}% korting`,
                estimatedPrice: deal.discountedPrice,
                originalPrice: deal.originalPrice,
                store: store?.name || 'Overig',
                quantity: quantityToBuy
              };
            }
            return null;
          }
          return {
            id: generateId(),
            itemId: item.id,
            name: item.name,
            quantity: quantityToBuy,
            unit: item.unit,
            category: item.category,
            reason: 'Goede aanbieding beschikbaar',
            deal: `${((deal.originalPrice - deal.discountedPrice) / deal.originalPrice * 100).toFixed(0)}% korting`,
            estimatedPrice: deal.discountedPrice,
            originalPrice: deal.originalPrice,
            store: store?.name || 'Overig'
          };
        }).filter(Boolean);
        itemsWithDeals.forEach((item: any) => {
          if (!suggestedItemsMap.has(item.name.toLowerCase())) {
            suggestedItemsMap.set(item.name.toLowerCase(), item);
          }
        });
      }
      const suggestedItems = Array.from(suggestedItemsMap.values());
      setGeneratedItems(suggestedItems);
      const initialSelection = suggestedItems.reduce((acc: any, item: any) => {
        acc[item.id] = true;
        return acc;
      }, {});
      setSelectedItems(initialSelection);
      setStep('review');
    } catch (error) {
      setError('Er is een fout opgetreden bij het genereren van de lijst');
      setStep('initial');
    } finally {
      setIsGenerating(false);
    }
  };
  const handleToggleItem = (itemId: string) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };
  const handleToggleAll = () => {
    const allSelected = Object.values(selectedItems).every(v => v);
    const newSelection = generatedItems.reduce((acc: any, item: any) => {
      acc[item.id] = !allSelected;
      return acc;
    }, {});
    setSelectedItems(newSelection);
  };
  const selectedCount = Object.values(selectedItems).filter(v => v).length;
  const groupedItems = generatedItems.reduce((acc: any, item: any) => {
    if (!acc[item.store]) acc[item.store] = [];
    acc[item.store].push(item);
    return acc;
  }, {});
  const handleComplete = () => {
    const itemsToAdd = generatedItems.filter(item => selectedItems[item.id]);
    onGenerate(itemsToAdd);
  };
  return <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-2015" data-mp="11de4h274pacp">
      <motion.div initial={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} className="bg-card rounded-2xl border border-border shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-2016" data-mp="mgdotx27p5ul">
        <div className="p-6 border-b border-border flex items-center justify-between shrink-0" data-id="element-2017" data-mp="2kd3w0wikq3sb">
          <div className="flex items-center gap-3" data-id="element-2018" data-mp="2fav1vwtysvom">
            <div className="p-2.5 bg-primary/10 rounded-xl" data-id="element-2019" data-mp="2ad380x9rezhs">
              <Brain className="w-5 h-5 text-primary" data-id="element-2020" data-mp="2admqqg78hrwv" />
            </div>
            <div data-id="element-2021" data-mp="26d2vxja4v0dw">
              <h2 className="text-lg font-semibold text-foreground" data-id="element-2022" data-mp="26dkq8ad8j4j6">
                Boodschappen aanvullen
              </h2>
              <p className="text-sm text-muted-foreground" data-id="element-2023" data-mp="2y2xmfj9q8w45">
                Kies wat je op je lijst wilt zetten.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-xl transition-colors" data-id="element-2024" data-mp="2tao1khngym8q">
            <X className="w-5 h-5" data-id="element-2025" data-mp="3ltjtb77zwack" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1" data-id="element-2026" data-mp="3h21timhpkft6">
          {step === 'initial' && <div className="space-y-6" data-id="element-2027" data-mp="13j5izey39e7j">
              <div className="flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/10 p-4" data-id="element-2028" data-mp="13k74yegn7l41">
                <Info className="mt-0.5 w-5 shrink-0 text-primary" data-id="element-2029" data-mp="18j1oxjqcjfpq" />
                <p className="text-sm text-primary" data-id="element-2030" data-mp="1cjbzzaszwwbg">
                  We kijken wat je waarschijnlijk nodig hebt. Jij kiest daarna zelf wat er op je lijst komt.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-2031" data-mp="1mit6tvjzhg90">
                <button onClick={() => setMode('smart')} className={`rounded-xl border-2 p-5 text-left transition-all ${mode === 'smart' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`} data-id="element-2032" data-mp="1rh4hg8i3rusa">
                  <Sparkles className={`mb-3 w-6 ${mode === 'smart' ? 'text-primary' : 'text-muted-foreground'}`} data-id="element-2033" data-mp="208mvacrktyi7" />
                  <h3 className={`mb-1 font-semibold ${mode === 'smart' ? 'text-primary' : 'text-foreground'}`} data-id="element-2034" data-mp="2a72kubaygzpl">
                    Met aanbiedingen
                  </h3>
                  <p className="text-sm text-muted-foreground" data-id="element-2035" data-mp="1rkr12pbg5tuz">
                    Vul aan en bekijk deals die bij je lijst passen.
                  </p>
                </button>

                <button onClick={() => setMode('basic')} className={`rounded-xl border-2 p-5 text-left transition-all ${mode === 'basic' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`} data-id="element-2036" data-mp="21b25ecz1m52e">
                  <Package className={`mb-3 w-6 ${mode === 'basic' ? 'text-primary' : 'text-muted-foreground'}`} data-id="element-2037" data-mp="18panwmctoyl7" />
                  <h3 className={`mb-1 font-semibold ${mode === 'basic' ? 'text-primary' : 'text-foreground'}`} data-id="element-2038" data-mp="1ik2jjkmuhbdc">
                    Alleen aanvullen
                  </h3>
                  <p className="text-sm text-muted-foreground" data-id="element-2039" data-mp="2tzhkezi6cndq">
                    Voeg alleen producten toe die bijna op zijn.
                  </p>
                </button>
              </div>

              {error && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl text-destructive text-sm flex items-center gap-2" data-id="element-2040" data-mp="36gs8l1h2pz8g">
                  <AlertTriangle className="w-4 h-4 shrink-0" data-id="element-2041" data-mp="2wjxcep89waxc" />
                  {error}
                </div>}
            </div>}

          {step === 'generating' && <div className="flex flex-col items-center justify-center py-12" data-id="element-2042" data-mp="3p9w5qwti2u7t">
              <Loader2 className="w-10 h-10 text-primary animate-spin mb-6" data-id="element-2043" data-mp="3bhy394uanwkm" />
              <h3 className="text-lg font-semibold text-foreground mb-2" data-id="element-2044" data-mp="3bfvf6qcpcdij">
                Even kijken wat je nodig hebt...
              </h3>
              <p className="max-w-sm text-center text-muted-foreground" data-id="element-2045" data-mp="7385pldb4x90">
                We maken alvast een paar praktische suggesties.
              </p>
            </div>}

          {step === 'review' && <div className="space-y-6" data-id="element-2046" data-mp="lpf3wc61i60m">
              <div className="flex items-center justify-between bg-muted rounded-xl p-3" data-id="element-2047" data-mp="lo44eojmnkfb">
                <button onClick={handleToggleAll} className="flex items-center gap-3 text-sm font-medium text-foreground hover:text-primary transition-colors" data-id="element-2048" data-mp="hmqwm8wz575r">
                  {selectedCount === generatedItems.length ? <CheckSquare className="w-5 h-5 text-primary" data-id="element-2049" data-mp="2t5l5bk70raa" /> : selectedCount > 0 ? <CheckSquare className="w-5 h-5 text-primary opacity-50" data-id="element-2050" data-mp="3u4yxjcamhqg7" /> : <Square className="w-5 h-5 text-muted-foreground" data-id="element-2051" data-mp="3q2kdwpz4qmnv" />}
                  <span data-id="element-2052" data-mp="3l7eupyx43m3h">
                    {selectedCount} van {generatedItems.length} geselecteerd
                  </span>
                </button>
                <span className="text-sm text-muted-foreground" data-id="element-2053" data-mp="3b6w32hzb31b0">
                  {Object.keys(groupedItems).length} winkels
                </span>
              </div>

              <div className="space-y-6" data-id="element-2054" data-mp="315tosumo1pdl">
                {Object.entries(groupedItems).map(([store, items]: [string, any]) => <div key={store} className="space-y-3" data-id="element-2055" data-mp="3b2okl5rk1bo6">
                      <div className="flex items-center gap-2 px-1" data-id="element-2056" data-mp="3g0geg5brf7v0">
                        <Store className="w-4 h-4 text-muted-foreground" data-id="element-2057" data-mp="3fynhxiuv240q" />
                        <h4 className="font-semibold text-foreground" data-id="element-2058" data-mp="2iealsnsn6bbk">
                          {store}
                        </h4>
                      </div>
                      <div className="space-y-2" data-id="element-2059" data-mp="2sbf9wt61sgv0">
                        {items.map((item: any) => <button key={item.id} onClick={() => handleToggleItem(item.id)} className={`w-full p-4 rounded-xl border text-left transition-all flex items-center gap-4 ${selectedItems[item.id] ? 'bg-card border-primary/30 shadow-sm' : 'bg-muted/30 border-border/50'}`} data-id="element-2060" data-mp="2x2dwubljkd78">
                            {selectedItems[item.id] ? <CheckSquare className="w-5 h-5 text-primary shrink-0" data-id="element-2061" data-mp="3es97gwzzeufr" /> : <Square className="w-5 h-5 text-muted-foreground shrink-0" data-id="element-2062" data-mp="3nr4ak07qboeu" />}

                            <div className="flex-1 min-w-0" data-id="element-2063" data-mp="5kzoxvhz9xpb">
                              <p className="font-medium text-foreground truncate" data-id="element-2064" data-mp="5jesu1g0m6pc">
                                {item.name}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5" data-id="element-2065" data-mp="fa9i7eok5mf0">
                                {item.reason}
                              </p>
                            </div>

                            <div className="flex items-center gap-3 shrink-0" data-id="element-2066" data-mp="pajumx2rqpt4">
                              {item.deal && <span className="px-2 py-1 bg-success/10 text-success text-xs font-medium rounded-full border border-success/20" data-id="element-2067" data-mp="kc8l6ngdyhbf">
                                  {item.deal}
                                </span>}
                              <span className="px-2.5 py-1 bg-background rounded-lg text-sm font-medium text-foreground border border-border" data-id="element-2068" data-mp="tyt0opaithmu">
                                {item.quantity}x
                              </span>
                            </div>
                          </button>)}
                      </div>
                    </div>)}
              </div>
            </div>}
        </div>

        <div className="p-6 border-t border-border bg-muted/30 flex justify-end gap-3 shrink-0" data-id="element-2069" data-mp="g6mofeahnuso">
          <button onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-xl transition-colors" data-id="element-2070" data-mp="19cg2dt57516">
            Annuleren
          </button>

          {step === 'initial' && <button onClick={generateList} className="px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-xl hover:bg-primary/90 transition-colors flex items-center gap-2 shadow-sm" data-id="element-2071" data-mp="5fee0jhf323h">
              <Sparkles className="w-4 h-4" data-id="element-2072" data-mp="f02uskirc18" />
              Bekijk suggesties
            </button>}

          {step === 'review' && <button onClick={handleComplete} disabled={selectedCount === 0} className="px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-xl hover:bg-primary/90 transition-colors flex items-center gap-2 shadow-sm disabled:opacity-50" data-id="element-2073" data-mp="o5m8kd71wk82">
              <Check className="w-4 h-4" data-id="element-2074" data-mp="172dsmouf33d6" />
              Toevoegen aan Lijst ({selectedCount})
            </button>}
        </div>
      </motion.div>
    </div>;
}